import { CheckBox, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import {
  copyStock,
  getRequestType,
  isPendingRequestItem,
  RequestTypes,
} from "./utils/utils";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { ViewType } from "@components/molecules/PriceModeCard/types";
import { useTheme } from "styled-components/native";
import { useSelectedItems } from "../../contexts/SelectedItemsContext";
import { useSelector } from "react-redux";
import { Pressable, View } from "react-native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PdpSections } from "@utils/customization";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { isMeleeItem } from "@utils/product/productValidators";

type ItemActionCardProps = {
  stockItemRequest: StockItemRequestClass;
  productType: any;
  setDisabledButton?: Dispatch<SetStateAction<boolean>>;
  validValue?: boolean;
  isModifyScreen?: boolean;
  displayCheckbox?: boolean;
  itemList?: any[];
  showListPrice?: boolean;
  isTotalOfferDiscountApplied?: boolean;
  hasAnyOfferDetails?: boolean;
  orderCartRequest?: boolean;
  fromRequestDetailScreen?: boolean;
  quoteItemSelection?: boolean;
};

const ItemActionCard = ({
  stockItemRequest,
  isModifyScreen = false,
  productType,
  setDisabledButton,
  showListPrice,
  isTotalOfferDiscountApplied,
  hasAnyOfferDetails,
  orderCartRequest,
  fromRequestDetailScreen = false,
  quoteItemSelection = false,
}: ItemActionCardProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const userInfo = useUserInfo();
  const { selectedItems, setSelectedItems } = useSelectedItems();
  const isBulkRequest = !!stockItemRequest?.children;
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const hasChildren = quoteItemSelection
    ? !!stockItemRequest?.children?.length
    : stockItemRequest?.secondary_request_type === PdpSections.JEWELRY_BUILDER
    ? false
    : !!stockItemRequest?.children?.length;
  const isTotalOfferPriceApplied =
    stockItemRequest?.quote_details?.total_offer_price_applied;
  const pendingChildren = useMemo(
    () =>
      stockItemRequest?.children
        ?.filter((item: StockItemRequestClass) => isPendingRequestItem(item))
        .map((item: StockItemRequestClass) => item.id) || [],
    [stockItemRequest?.children, quoteItemSelection]
  );
  const selectableChildIds = pendingChildren;
  const selectableIdsKey = selectableChildIds.join(",");

  const checkOfferApplied = () => {
    const itemTotal = Object.values(stockItemRequest?.children).reduce(
      (sum, item: any) =>
        sum +
        Number(
          item?.updated_total_sales_price || item?.offer_total_sales_price
        ),
      0
    );
    return (
      formatNumberWithPrecision(stockItemRequest?.offer_total_sales_price, {
        precision:
          userInfo?.roundUpPrices() &&
          !isMeleeItem(stockItemRequest?.[productType.productType])
            ? 0
            : 2,
      })?.replaceAll(",", "") !==
      formatNumberWithPrecision(itemTotal, {
        precision:
          userInfo?.roundUpPrices() &&
          !isMeleeItem(stockItemRequest?.[productType.productType])
            ? 0
            : 2,
      })?.replaceAll(",", "")
    );
  };

  const Wrapper =
    (showListPrice || isTotalOfferPriceApplied || checkOfferApplied()) &&
    isTotalOfferDiscountApplied === false &&
    !hasAnyOfferDetails
      ? Pressable
      : View;

  const showCheckbox = useMemo(
    () =>
      hasChildren &&
      (quoteItemSelection || selectableChildIds.length > 0) &&
      (quoteItemSelection ||
        stockItemRequest?.view_type !== ViewType.BuyerView ||
        isModifyScreen ||
        fromRequestDetailScreen),
    [
      hasChildren,
      selectableChildIds,
      quoteItemSelection,
      stockItemRequest?.view_type,
      isModifyScreen,
      fromRequestDetailScreen,
    ]
  );

  const isAllSelected = useMemo(() => {
    if (!hasChildren || selectableChildIds.length === 0) return false;
    return selectableChildIds.every((id) => selectedItems.includes(id));
  }, [hasChildren, selectableChildIds, selectedItems]);

  useEffect(() => {
    setDisabledButton?.(isBulkRequest ? !selectedItems.length : false);
  }, [isBulkRequest, selectedItems.length]);

  const handleSelectAll = () => {
    if (!hasChildren) return;
    if (quoteItemSelection && selectableChildIds.length === 0) return;
    if (!quoteItemSelection && stockItemRequest?.status !== "pending") {
      return;
    }
    if (
      stockItemRequest?.additional_attributes?.total_offer_price_applied ||
      getRequestType(stockItemRequest?.request_type) === RequestTypes.Buy ||
      getRequestType(stockItemRequest?.request_type) === RequestTypes.Quote
    ) {
      if (
        (isTotalOfferPriceApplied || checkOfferApplied()) &&
        isTotalOfferDiscountApplied === false &&
        !hasAnyOfferDetails
      ) {
        showToast(
          "",
          getRequestType(stockItemRequest?.request_type) === RequestTypes.Quote
            ? t("requestHistoryDetail.quote.quoteWarning.text")
            : t("requestHistoryDetail.buy.buyWarning.text"),
          ToastTypes.ERROR,
          true,
          true,
          80
        );
        return;
      }
    }

    if (isAllSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(selectableChildIds);
    }
  };

  useEffect(() => {
    if (hasChildren) {
      setSelectedItems(selectableChildIds);
    } else {
      setSelectedItems([]);
    }
    // Order details build a new stockItemRequest object on every parent render.
    // Depend on the request id and selectable line ids so a deselect is kept.
  }, [stockItemRequest?.id, selectableIdsKey, hasChildren]);

  const getStockNumber = (item) => {
    const stockNum =
      enableJewelryBuilder && !orderCartRequest
        ? item[item.stock_item_type].stock_num
        : orderCartRequest
        ? item?.stock_num
        : item[productType.productType].stock_num;
    const parseStockNum = () => {
      if (!stockNum) return "";
      if (stockNum.includes("|")) {
        return stockNum
          .split("|")
          .map((num) => num.trim())
          .filter(Boolean);
      }
      return stockNum.trim();
    };
    return (
      (!isCustomApp && item.bmh_request_type !== "retail_buy_request"
        ? "VDB-"
        : "") + parseStockNum()
    );
  };

  return (
    <ActionsView displayCheckbox={showCheckbox ?? false}>
      {showCheckbox && (
        <CheckboxView showCheckbox={showCheckbox}>
          <Wrapper
            onPress={
              (isTotalOfferPriceApplied || checkOfferApplied()) &&
              isTotalOfferDiscountApplied === false &&
              !hasAnyOfferDetails
                ? handleSelectAll
                : undefined
            }
          >
            <CheckBox
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
              checkedFunction={handleSelectAll}
              isChecked={isAllSelected}
              disabled={
                quoteItemSelection
                  ? selectableChildIds.length === 0
                  : (isTotalOfferPriceApplied || checkOfferApplied()) &&
                    isTotalOfferDiscountApplied === false &&
                    !hasAnyOfferDetails
              }
            />
          </Wrapper>
          <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
            {t("requestHistoryDetail.selectionInfo.text", {
              selected: selectedItems.length,
              total: stockItemRequest.children.length,
            })}
          </VDBText>
        </CheckboxView>
      )}

      <CopyStockContainer
        onPress={() => {
          copyStock(
            stockItemRequest,
            hasChildren,
            getStockNumber,
            t,
            enableJewelryBuilder,
            orderCartRequest,
            selectedItems
          );
        }}
      >
        <IconSlot>
          <VDBIcon
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.COPY}
            size={20}
            color={theme["main-01"]}
          />
        </IconSlot>
        <VDBText
          type={
            theme.isLargeScreen ? typography["Body-R"] : typography["Minor-R"]
          }
          color={theme["core-06"]}
        >
          {t("requestHistoryDetail.copyStock.text")}
        </VDBText>
      </CopyStockContainer>
    </ActionsView>
  );
};

const ActionsView = styled.View<{ displayCheckbox: boolean }>`
  ${(displayCheckbox) =>
    displayCheckbox
      ? css`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          min-height: 40px;
          padding-top: ${SpaceType.Double}px;
          padding-bottom: ${SpaceType.Double}px;
        `
      : ""}
`;
const CheckboxView = styled.View<{
  showCheckbox?: boolean;
}>`
  padding-left: ${({ showCheckbox, theme }) =>
    showCheckbox
      ? theme.isMobile
        ? `${SpaceType.ExtraSmall}px`
        : `${SpaceType.Basic}px`
      : 0};
  align-items: center;
  align-self: center;
  flex-direction: row;
`;

const CopyStockContainer = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  align-self: center;
  justify-content: flex-end;
  gap: ${SpaceType.ExtraSmall}px;
`;

const IconSlot = styled.View`
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
`;

export default ItemActionCard;
